import {
  forwardRef,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cx } from '../lib/cx.js';
import type { ControlSize } from './Button.js';
import { useField } from './Field.js';
import './field.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  size?: ControlSize | undefined;
  /** Icon or text before the value (e.g. a search icon or "$"). */
  prefix?: ReactNode | undefined;
  /** Icon, unit or action after the value. */
  suffix?: ReactNode | undefined;
  invalid?: boolean | undefined;
  /** Right-aligned tabular numerals for money and counts. */
  numeric?: boolean | undefined;
  /** Class for the outer box; `className` goes on the <input>. */
  boxClassName?: string | undefined;
  /** Inline style for the outer box (e.g. a fixed width). */
  boxStyle?: CSSProperties | undefined;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = 'md',
    prefix,
    suffix,
    invalid,
    numeric,
    boxClassName,
    boxStyle,
    className,
    id,
    disabled,
    ...rest
  },
  ref,
) {
  const field = useField();
  const isInvalid = invalid ?? field?.invalid ?? false;
  return (
    <div
      className={cx('vel-input', boxClassName)}
      style={boxStyle}
      data-size={size}
      data-invalid={isInvalid ? '' : undefined}
      data-disabled={disabled ? '' : undefined}
      data-numeric={numeric ? '' : undefined}
    >
      {prefix ? <span className="vel-input__adorn">{prefix}</span> : null}
      <input
        ref={ref}
        id={id ?? field?.id}
        disabled={disabled}
        aria-invalid={isInvalid || undefined}
        aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
        aria-required={rest['aria-required'] ?? (field?.required || undefined)}
        className={cx('vel-input__control', className)}
        {...rest}
      />
      {suffix ? <span className="vel-input__adorn">{suffix}</span> : null}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean | undefined;
  /** Reading-surface typography (16px, 1.65) for notes and letters. */
  reading?: boolean | undefined;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, reading, className, id, ...rest },
  ref,
) {
  const field = useField();
  const isInvalid = invalid ?? field?.invalid ?? false;
  return (
    <textarea
      ref={ref}
      id={id ?? field?.id}
      aria-invalid={isInvalid || undefined}
      aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
      className={cx('vel-textarea', className)}
      data-reading={reading ? '' : undefined}
      {...rest}
    />
  );
});
